import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Scissors, ArrowLeft, Building2, Users, CalendarDays } from "lucide-react";
import { useAuth } from "@/lib/auth";

const STATUS_TINT = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  trialing: "bg-amber-50 text-amber-800 border-amber-200",
  past_due: "bg-rose-50 text-rose-700 border-rose-200",
  canceled: "bg-stone-100 text-stone-700 border-stone-200",
  none: "bg-stone-100 text-stone-600 border-stone-200",
};

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [groomers, setGroomers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    if (user.role !== "super_admin") { nav("/"); return; }
    api.get("/admin/groomers")
      .then(({ data }) => setGroomers(data))
      .catch(() => toast.error("Couldn't load groomers"))
      .finally(() => setLoading(false));
  }, [user, nav]);

  const activeCount = groomers.filter(g => ["active", "trialing"].includes(g.subscription_status)).length;
  const mrrEstimate = groomers.filter(g => g.subscription_status === "active").length * 29;

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#1F1A17]">
      <header className="border-b border-[#E5DCCE] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#2D4A3E] grid place-content-center text-white">
              <Scissors className="w-4 h-4"/>
            </div>
            <div>
              <div className="font-display font-bold leading-none">PawTrek</div>
              <div className="text-[10px] text-[#6B625B] uppercase tracking-wider mt-1">Super Admin</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => nav("/groomer")} data-testid="admin-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-1"/> My dashboard
            </Button>
            <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/"); }}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-6 space-y-6">
        <div>
          <div className="label-accent">Platform</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Groomers on PawTrek</h1>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Stat icon={Building2} label="Total groomers" value={groomers.length} tint="#F5E6CF" fg="#A9480F"/>
          <Stat icon={Users} label="Active subs" value={activeCount} tint="#D8EFDF" fg="#166534"/>
          <Stat icon={CalendarDays} label="MRR (est)" value={`$${mrrEstimate}`} tint="#DDE7E3" fg="#21382E"/>
        </div>

        <div className="bg-white rounded-3xl border border-[#E5DCCE] shadow-sm p-6">
          <h2 className="font-display text-xl font-bold mb-3">All groomer accounts</h2>
          {loading ? <p className="text-sm text-[#6B625B]">Loading…</p> :
            groomers.length === 0 ? <p className="text-sm text-[#6B625B]">No groomer sign-ups yet.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-[#6B625B] border-b border-[#E5DCCE]">
                    <th className="py-2 pr-4">Business</th>
                    <th className="py-2 pr-4">Owner</th>
                    <th className="py-2 pr-4">Email</th>
                    <th className="py-2 pr-4 text-center">Clients</th>
                    <th className="py-2 pr-4 text-center">Appts</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {groomers.map(g => (
                    <tr key={g.id} className="border-b border-[#F3EDE4] last:border-0" data-testid={`admin-row-${g.id}`}>
                      <td className="py-2 pr-4 font-semibold">{g.business_name || "—"}</td>
                      <td className="py-2 pr-4">{g.name}</td>
                      <td className="py-2 pr-4 text-[#6B625B]">{g.email}</td>
                      <td className="py-2 pr-4 text-center">{g.clients_count}</td>
                      <td className="py-2 pr-4 text-center">{g.appointments_count}</td>
                      <td className="py-2">
                        <span className={`pill border text-[10px] ${STATUS_TINT[g.subscription_status] || STATUS_TINT.none}`}>
                          {g.subscription_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tint, fg }) {
  return (
    <div className="bg-white rounded-3xl border border-[#E5DCCE] p-5">
      <div className="w-10 h-10 rounded-xl grid place-content-center mb-3" style={{ background: tint, color: fg }}>
        <Icon className="w-5 h-5"/>
      </div>
      <div className="text-xs uppercase tracking-wider text-[#6B625B] font-semibold">{label}</div>
      <div className="font-display text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
